import * as styles from './panel.css';

import { PanelContext } from '@nrdy-marketing-engine/form-context';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { PanelData, PanelStateProps } from '../@types/panel-data';
import { StepData } from '../@types/step-data';
import LeadFormHeadsImage from './lead-form-heads-image';
import ContentPanel from './panel/content-panel';
import FormInputPanel from './panel/form-input-panel';
import InterstitialPanel from './panel/interstitial-panel';
import MultipleChoice from './panel/multiple-choice-panel';
import PlaceholderProgressBar from './progress-bar/placeholder-progress-bar';

const InformationalContentDynamic = dynamic(() => import('./informational-content'));
const ProgressBarDynamic = dynamic(() => import('./progress-bar'), {
  loading: () => <PlaceholderProgressBar />,
});

const getComponentMap = (): Record<string, (PanelProps) => JSX.Element> => ({
  panel_content: ContentPanel,
  panel_form_input: FormInputPanel,
  panel_interstitial: InterstitialPanel,
  panel_multiple_choice: MultipleChoice,
});

/** Returns true if the given step is a panel (visual step with UI) as opposed to an action. */
export function isPanel(step: StepData): step is PanelData {
  const map = getComponentMap();
  return Object.keys(map).includes(step._modelApiKey);
}

interface PanelComponentProps extends PanelData, PanelStateProps {
  currentStepIndex: number;
  maxSteps: number;
  showLeadImage?: boolean;
  showProgressBar?: boolean;
}

/**
 * Represents any form step that is rendered as a visual panel with UI). It reads the props
 * to determine the internal components to render.
 */
function PanelComponent(props: PanelComponentProps) {
  const componentMap = getComponentMap();

  const centered = 'nmeCentered' in props && props.nmeCentered === true;
  const showProgressBar = props.showProgressBar && props.currentStepIndex > 1;

  if (Object.keys(componentMap).includes(props._modelApiKey)) {
    const Component = componentMap[props._modelApiKey];

    return (
      <PanelContext.Provider value={props}>
        <div
          className={clsx(
            styles.panel,
            props.showLeadImage && styles.panelWithImage,
            props.informationalContent && styles.panelWithInformationalContent,
            !showProgressBar && styles.panelWithoutProgressBar
          )}
        >
          {showProgressBar && (
            <div className={styles.revealAnimation}>
              <ProgressBarDynamic currentStep={props.currentStepIndex} steps={props.maxSteps} />
            </div>
          )}
          <Component {...props} />
          {props.showLeadImage && <LeadFormHeadsImage centered={centered} />}
          {props.informationalContent && (
            <InformationalContentDynamic {...props.informationalContent} />
          )}
        </div>
      </PanelContext.Provider>
    );
  }

  // TODO: We should be warned if an unexpected panel type is received
  return null;
}

export default PanelComponent;
